import { useRef, useState, type ChangeEvent, type PointerEvent } from "react";
import { Box, Typography, TextField, Button } from "@mui/material";
import {
  containerStyles,
  headerRowStyles,
  headerDividerStyles,
  titleStyles,
  subtitleStyles,
  questionStyles,
  ratingQuestionStyles,
  starsRowStyles,
  starButtonStyles,
  starsLabelsRowStyles,
  textFieldStyles,
  counterStyles,
  chipsRowStyles,
  chipStyles,
  submitButtonStyles,
  privacyRowStyles,
  errorMessageStyles,
  headerIconBubbleStyles,
  issueTitleStyles,
  attachRowStyles,
  attachButtonStyles,
  attachFileNameStyles,
  attachErrorStyles,
} from "./styles";
import { FaStar, FaRegStar } from "react-icons/fa";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import {
  isBrowserOffline,
  notifyOfflineActionBlocked,
} from "../../../../utils/internetConnection";
import { useAppStore } from "../../../../stores/appStore";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import GpsFixedRoundedIcon from "@mui/icons-material/GpsFixedRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ThumbDownOffAltRoundedIcon from "@mui/icons-material/ThumbDownOffAltRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { sendFeedback } from "../../../../axios/feedback";

interface FeedbackInputProps {
  title?: string;
  text: string;
  isTextInput: boolean;
  starsCount: number;
  initialSelectedStars?: number;
  onSubmit: () => void;
}

export const FeedbackInput = ({
  title = "",
  text,
  isTextInput,
  starsCount,
  initialSelectedStars,
  onSubmit,
}: FeedbackInputProps) => {
  if (!initialSelectedStars) {
    initialSelectedStars = 0;
  }

  const [inputText, setInputText] = useState("");
  const [selectedStars, setSelectedStars] = useState(initialSelectedStars);
  const [starsError, setStarsError] = useState<string | null>(null);
  const [hoveredStars, setHoveredStars] = useState<number | null>(null);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isOnline = useAppStore((s) => s.isOnline);
  const displayStars = hoveredStars ?? selectedStars;
  const MAX_FEEDBACK_LENGTH = 500;
  const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;
  const reasonOptions = [
    { label: "Answer accuracy", icon: <GpsFixedRoundedIcon sx={{ fontSize: 17 }} /> },
    { label: "Too slow", icon: <AccessTimeRoundedIcon sx={{ fontSize: 17 }} /> },
    {
      label: "Not helpful",
      icon: <ThumbDownOffAltRoundedIcon sx={{ fontSize: 17 }} />,
    },
    { label: "Other", icon: <MoreHorizRoundedIcon sx={{ fontSize: 17 }} /> },
  ];

  const handleCancelStarsSelection = () => setHoveredStars(null);

  const handleStarsSelection = (value: number) => {
    if (!isOnline || isBrowserOffline()) {
      notifyOfflineActionBlocked();
      return;
    }
    setStarsError(null);
    setHoveredStars(value);
  };

  const handleStarsSelectionEnd = (value: number) => {
    if (!isOnline || isBrowserOffline()) {
      notifyOfflineActionBlocked();
      return;
    }
    setSelectedStars(value);
    setHoveredStars(null);
  };

  const handleStarPointerEnter = (
    value: number,
    event: PointerEvent<HTMLButtonElement>,
  ) => {
    if (event.pointerType === "mouse") {
      handleStarsSelection(value);
    }
  };

  const handleStarPointerDown = (
    value: number,
    event: PointerEvent<HTMLButtonElement>,
  ) => {
    if (event.pointerType !== "mouse") {
      event.preventDefault();
      handleStarsSelectionEnd(value);
    }
  };

  const handleSubmit = async () => {
    if (!isOnline || isBrowserOffline()) {
      notifyOfflineActionBlocked();
      return;
    }
    if (
      !selectedStars ||
      selectedStars <= 0 ||
      !Number.isInteger(selectedStars)
    ) {
      setStarsError(
        "Please select star rating before submitting your feedback",
      );
      return;
    }

    try {
      await sendFeedback({
        input: inputText.trim(),
        selected_stars: selectedStars,
        reason_label: selectedReason || undefined,
        reason: selectedReason
          ? selectedReason.toLowerCase().replace(/\s+/g, "_")
          : undefined,
        file: selectedFile || undefined,
      });
      onSubmit();
    } catch (error) {
      console.error("Feedback submission error: ", error);
    } finally {
      setInputText("");
      setSelectedStars(0);
      setHoveredStars(null);
      setSelectedReason(null);
      setSelectedFile(null);
      setFileError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0];
    if (!nextFile) {
      setSelectedFile(null);
      setFileError(null);
      return;
    }

    if (nextFile.size > MAX_ATTACHMENT_SIZE_BYTES) {
      setSelectedFile(null);
      setFileError("File is too large. Max size is 10 MB.");
      event.target.value = "";
      return;
    }

    setSelectedFile(nextFile);
    setFileError(null);
  };

  if (
    initialSelectedStars < 0 ||
    initialSelectedStars > starsCount ||
    !Number.isInteger(initialSelectedStars) ||
    !Number.isInteger(starsCount)
  ) {
    console.error("Invalid value for stars");
    return null;
  }

  return (
    <Box sx={containerStyles}>
      <Box sx={headerRowStyles}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
          <Box sx={headerIconBubbleStyles}>
            <HelpOutlineRoundedIcon sx={{ fontSize: 19, color: "#FFFFFF" }} />
          </Box>
          <Box>
            <Typography sx={titleStyles} variant="h4">
              Send Help Request
            </Typography>
            <Typography sx={subtitleStyles}>
              Describe your question or issue. Our team will get back to you.
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box component="hr" sx={headerDividerStyles} />

      <Typography sx={questionStyles}>
        What do you need help with?{" "}
      </Typography>

      {isTextInput && (
        <Box>
          <Box sx={{ position: "relative" }}>
            <TextField
              fullWidth
              variant="outlined"
              multiline
              minRows={4}
              inputProps={{ maxLength: MAX_FEEDBACK_LENGTH }}
              placeholder="Type your question or describe the issue you're facing..."
              value={inputText}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setInputText(e.target.value);
              }}
              sx={textFieldStyles}
            />
            <Box sx={attachRowStyles}>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <Box
                sx={attachButtonStyles}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                <AttachFileRoundedIcon sx={{ fontSize: 20 }} />
                <span>
                  {selectedFile ? "Change file" : "Attach file (optional)"}
                </span>
              </Box>
              {selectedFile && (
                <Typography sx={attachFileNameStyles}>{selectedFile.name}</Typography>
              )}
              <Typography sx={counterStyles}>
                {inputText.length}/{MAX_FEEDBACK_LENGTH}
              </Typography>
            </Box>
          </Box>
          {fileError && <Typography sx={attachErrorStyles}>{fileError}</Typography>}
        </Box>
      )}

      <Typography sx={issueTitleStyles}>
        What&apos;s the issue related to?
      </Typography>

      <Box sx={chipsRowStyles}>
        {reasonOptions.map((option) => {
          const isSelected = selectedReason === option.label;
          return (
            <Button
              key={option.label}
              onClick={() => setSelectedReason(isSelected ? null : option.label)}
              sx={chipStyles(isSelected)}
              startIcon={option.icon}
            >
              {option.label}
            </Button>
          );
        })}
      </Box>

      <Typography sx={ratingQuestionStyles}>
        How would you rate your overall experience?
      </Typography>

      {starsCount && (
        <Box sx={starsRowStyles} onMouseLeave={handleCancelStarsSelection}>
          {new Array(starsCount).fill(null).map((_, index) => {
            const value = index + 1;
            const isActive = value <= displayStars;

            return (
              <Button
                key={value}
                onPointerEnter={(event) => handleStarPointerEnter(value, event)}
                onPointerDown={(event) => handleStarPointerDown(value, event)}
                onClick={() => handleStarsSelectionEnd(value)}
                sx={starButtonStyles(isActive)}
              >
                {isActive ? <FaStar size={34} /> : <FaRegStar size={34} />}
              </Button>
            );
          })}
        </Box>
      )}

      <Box sx={starsLabelsRowStyles}>
        <span>Very bad</span>
        <span>Excellent</span>
      </Box>

      {starsError && (
        <Typography sx={errorMessageStyles}>{starsError}</Typography>
      )}

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={!selectedStars || selectedStars <= 0}
        sx={submitButtonStyles}
        startIcon={<SendRoundedIcon />}
      >
        Send help request
      </Button>

      <Box sx={privacyRowStyles}>
        <LockOutlinedIcon sx={{ fontSize: 18 }} />
        <span>Your request is private and secure</span>
      </Box>
    </Box>
  );
};
